import { parseIdMap } from './eg-configurator-state.js';
import { closeModalWithExit } from './eg-modal-exit.js';
import { SKIP_REASON, buildSteps, createLadder } from './eg-ladder-state.js';

/**
 * Pre-checkout ladder — DOM wiring only (U11 / R17, R20). Transitions and
 * selection live in eg-ladder-state.js (the tested module); the id map is
 * parsed by the configurator's own `parseIdMap`, so the two surfaces cannot
 * disagree about what a colorway costs or whether it is in stock.
 *
 * NO NETWORK ON THE CRITICAL PATH (R20, amended 2026-07-30). This module used
 * to await the app's shared offer config through the App Proxy before it could
 * decide whether to open at all — a ~2s budget, a timeout, an abort controller
 * and a duplicate-submit guard, all spent learning which cover products to
 * show. `eg-id-map` already says. The steps are now built synchronously at
 * init from markup Liquid rendered into the page, so the dialog opens in the
 * submit handler's own tick.
 *
 * The Kit CTA is a real product form: with JS off, or if this module fails to
 * load, submitting it adds the Kit and lands on /cart. Every failure path here
 * ends in that same native submit — the ladder can cost an upsell, never a sale.
 *
 * COMPONENT REUSE (Kyle, 2026-07-29): the stage and the colorway cards are the
 * CONFIGURATOR'S components, emitted with the configurator's class names and
 * styled by component-eg-configurator.css — not copies. `.eg-configurator__hero
 * -image`, `__thumb*`, `__cell`, `__radio`, `__swatch--card`, `__swatch-image`,
 * `__swatch-text`, `__option-name`, `__option-price`, `__reason`. Retuning a
 * cover card in the configurator retunes it here for free, and the circle track
 * arrives here with whatever shape that surface gives it. component-eg-ladder
 * .css owns only the modal frame and this surface's own layout.
 *
 * The sibling imports above resolve against the theme's flat /assets/ directory
 * on the Shopify CDN (which serves `access-control-allow-origin: *`, required
 * for cross-origin module scripts).
 */

const SELECTORS = {
  root: '[data-eg-ladder]',
  dialog: '[data-eg-ladder-dialog]',
  title: '[data-eg-ladder-title]',
  intro: '[data-eg-ladder-intro]',
  hero: '[data-eg-ladder-hero]',
  thumbs: '[data-eg-ladder-thumbs]',
  options: '[data-eg-ladder-options]',
  accept: '[data-eg-ladder-accept]',
  decline: '[data-eg-ladder-decline]',
  back: '[data-eg-ladder-back]',
  trigger: '[data-eg-ladder-trigger]',
  idMap: '#eg-id-map',
  copy: '[data-eg-ladder-copy]',
};

function log(message, detail) {
  // Skips are expected operationally (config outage, master flag OFF) — they
  // are logged, never surfaced to the shopper.
  console.info(`[eg-ladder] ${message}`, detail ?? '');
}

/**
 * PostHog / analytics dispatch (U22 amendment, 2026-09-02) — same pattern as
 * eg-configurator.js: no declaring element, so a DOM CustomEvent stands in
 * for `data-eg-event`, and eg-analytics.js relays it. Fails silent: the
 * ladder's own contract is that nothing about an upsell may cost the sale,
 * and that includes a broken analytics call.
 */
function track(name, params) {
  try {
    document.dispatchEvent(new CustomEvent('eg:track', { detail: { name, params: params || {} } }));
  } catch {
    /* analytics must never break the storefront */
  }
}

/** Tolerant JSON read: a missing or malformed block degrades, never throws. */
function readJson(selector) {
  const node = document.querySelector(selector);
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    log(`could not parse ${selector}`, error);
    return null;
  }
}

async function addVariantToCart(variantId) {
  const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
  });
  if (!response.ok) throw new Error(`cart/add.js responded ${response.status}`);
  return response.json();
}

/** Placeholder art, mirroring the configurator's own: a missing image must not
 *  break the stage or leave an empty box where a photo belongs. */
function imageNode(url, label, className) {
  if (typeof url === 'string' && url !== '') {
    const img = document.createElement('img');
    img.className = className;
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    return img;
  }
  const placeholder = document.createElement('span');
  placeholder.className = `${className} eg-configurator__placeholder`;
  placeholder.textContent = (label || '').slice(0, 1).toUpperCase();
  placeholder.setAttribute('aria-hidden', 'true');
  return placeholder;
}

function textSpan(className, text) {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = text;
  return span;
}

function initLadder(root) {
  const dialog = root.querySelector(SELECTORS.dialog);
  const titleEl = root.querySelector(SELECTORS.title);
  const introEl = root.querySelector(SELECTORS.intro);
  const heroEl = root.querySelector(SELECTORS.hero);
  const thumbsEl = root.querySelector(SELECTORS.thumbs);
  const optionsEl = root.querySelector(SELECTORS.options);
  const acceptBtn = root.querySelector(SELECTORS.accept);
  const declineBtn = root.querySelector(SELECTORS.decline);
  const backBtn = root.querySelector(SELECTORS.back);
  const forms = document.querySelectorAll(SELECTORS.trigger);

  if (!dialog || !titleEl || !optionsEl || !acceptBtn || !declineBtn || forms.length === 0) return;
  // Older browsers without <dialog> get the plain form (no un-trapped modal).
  if (typeof dialog.showModal !== 'function') return;

  const currencySymbol = root.dataset.currencySymbol || '$';

  // All read once at init: the id map and the per-cover copy are rendered by
  // Liquid into the page and never change during a session.
  const rawIdMap = readJson(SELECTORS.idMap);
  const idMap = rawIdMap ? parseIdMap(rawIdMap) : null;
  const copy = readJson(SELECTORS.copy) ?? {};

  // FAILS OPEN AT INIT (R17). A missing, malformed or coverless id map yields
  // no steps, so `initLadder` wires nothing and the Kit CTA stays the plain
  // product form it already is — Kit added, shopper on /cart. The try/catch is
  // the same posture the awaited config fetch used to hold: nothing about an
  // upsell may cost the sale.
  let steps = [];
  try {
    steps = buildSteps(idMap, { currencySymbol });
  } catch (error) {
    log('could not build steps from the id map; the ladder is skipped', error);
    steps = [];
  }
  if (steps.length === 0) {
    log('skipped', SKIP_REASON.NO_OFFERS);
    return;
  }

  let ladder = null;
  let pendingForm = null;
  let busy = false;
  // Which gallery photo the stage is aimed at, when the shopper has clicked a
  // circle. `{key, src}` — the key scopes it to one colorway's gallery so it
  // retires the moment she picks a different one. Null means "the first photo".
  let stageOverride = null;

  /** The only exit: hand the Kit back to its native form post (adds + /cart). */
  function proceed() {
    // Deferred close so the exit fade is seen (eg-modal-exit.js). The form
    // submit below navigates, which cuts the fade short — accepted rather than
    // worked around: this path is leaving the page anyway, and holding a
    // shopper's add-to-cart back by 240ms to finish an animation would be the
    // wrong trade.
    if (dialog.open) closeModalWithExit(dialog);
    if (pendingForm) pendingForm.submit();
  }

  function setBusy(value) {
    busy = value;
    // `canAdd` — not `busy` alone — owns the accept button's disabled state, so
    // clearing busy must never re-enable it while nothing is picked.
    acceptBtn.disabled = value || !ladder?.state().canAdd;
    declineBtn.disabled = value;
    if (backBtn) backBtn.disabled = value;
    dialog.setAttribute('aria-busy', String(value));
  }

  /** Hero + circle track for the step's current selection. Before she picks,
   *  the stage shows the first colorway that is actually buyable — never a
   *  sold-out one, which would advertise what she cannot have.
   *
   *  Follows the CONFIGURATOR'S CURRENT STAGE CONTRACT (design pass
   *  2026-07-29): a blurred cover-fit copy of the hero behind the contain-fit
   *  hero, and every circle a real button that swaps which photo the hero
   *  shows. Both arrived on that surface after this one was first built; the
   *  markup and classes are its, so the CSS came across on the rebase and only
   *  this emitter needed to catch up. */
  function renderStage(state) {
    const step = state.step;
    // NEVER A SOLD-OUT COLORWAY (2026-07-31). The old last resort was
    // `step.colorways[0]`, which on a step whose every colorway is sold out
    // aimed the stage at photos of something she cannot buy in any color.
    // Null falls through to the placeholder art instead — the same degrade
    // the imageNode helper already owes a missing photo.
    const chosen = state.selected ?? step.colorways.find((way) => way.selectable) ?? null;
    const images = chosen && chosen.images.length > 0 ? chosen.images : [chosen?.image].filter(Boolean);

    // A hero swap belongs to ONE colorway's gallery. Changing colorway (or
    // step) retires it, so a click on Cranberry's third photo cannot leave
    // Cinnamon's stage aimed at an image that is not Cinnamon's.
    const galleryKey = `${step.key}:${chosen?.variantId ?? 'none'}`;
    if (stageOverride && stageOverride.key !== galleryKey) stageOverride = null;
    const heroSrc = stageOverride?.src ?? images[0] ?? null;

    if (heroEl) {
      // The same src twice: a heavily blurred cover-fit copy floods the stage
      // behind the contain-fit hero, so letterboxed space reads as the photo's
      // own ambience rather than an empty panel. Decorative only.
      const backdrop = imageNode(heroSrc, '', 'eg-configurator__hero-backdrop');
      backdrop.setAttribute('aria-hidden', 'true');
      heroEl.replaceChildren(
        backdrop,
        imageNode(heroSrc, `${step.title} — ${chosen?.title ?? ''}`, 'eg-configurator__hero-image')
      );
    }

    if (thumbsEl) {
      // The gallery IN FULL, hero included, so she can always swap back —
      // `images.slice(1)` was the old inert-inset contract. The clay ring
      // marks whichever photo the hero is showing.
      const keptScrollLeft = thumbsEl.scrollLeft;
      thumbsEl.replaceChildren(
        ...images.map((src) => {
          const li = document.createElement('li');
          li.className = 'eg-configurator__thumb';
          const selected = src === heroSrc;
          if (selected) li.classList.add('eg-configurator__thumb--selected');

          const pick = document.createElement('button');
          pick.type = 'button';
          pick.className = 'eg-configurator__thumb-button';
          pick.setAttribute('aria-label', 'Show this photo');
          pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
          pick.addEventListener('click', () => {
            // Aims the stage only. It must NEVER pick a colorway — the right
            // column owns that, and a photo swap that quietly changed what
            // goes in the cart would be the worst kind of surprise on a $180
            // line.
            stageOverride = { key: galleryKey, src };
            render();
          });
          pick.appendChild(imageNode(src, step.title, 'eg-configurator__thumb-image'));

          li.appendChild(pick);
          return li;
        })
      );
      thumbsEl.scrollLeft = keptScrollLeft;
    }
  }

  function renderOptions(state) {
    const step = state.step;
    const name = `eg-ladder-${step.key}`;

    // The configurator's colorway grid, verbatim. `.eg-swatch-grid` supplies
    // the columns and the gap, `.eg-swatch` is the card — one BEM block for
    // every option cell since the 2026-07-30 rename; `__swatch--card` is gone
    // because cards are top-down everywhere now, so there is no variant to opt
    // into. All of it from component-eg-configurator.css.
    const grid = document.createElement('div');
    grid.className = 'eg-swatch-grid';
    // FOUR ACROSS ON THIS SURFACE (Kyle's explicit call for the ladder). The
    // shared grid now defaults to TWO — set for the configurator's own narrower
    // rail — and `--eg-cfg-swatch-columns` is the dial it reads, so this states
    // the ladder's own answer rather than inheriting a default that moved.
    grid.style.setProperty('--eg-cfg-swatch-columns', '4');

    grid.append(
      // OUT OF STOCK NEVER REACHES THE DOM, matching the configurator's own
      // `radioCell` guard (Kyle, 2026-07-29, superseding the greyed-and-
      // labelled R9 treatment this surface shipped with). The state layer still
      // carries every colorway with its `selectable` flag — that is what the
      // suite asserts, and what keeps a sold-out variant from ever being
      // selectable by a scripted path — but the picker only draws what she can
      // actually buy. The configurator's "except one the cart already holds"
      // exception has no analogue here: this runs before the Kit is even added,
      // so the cart can hold no cover yet.
      ...step.colorways.filter((way) => way.selectable).map((way) => {
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.id = `${name}-${way.variantId}`;
        input.className = 'eg-configurator__radio';
        input.checked = way.selected;
        input.setAttribute('aria-label', `${step.title} — ${way.title}`);
        input.addEventListener('change', () => {
          ladder.select(way.variantId);
          render();
        });

        // No `disabled` and no sold-out note: the filter above means every card
        // built here is buyable. `ladder.select()` still refuses an unselectable
        // variant, so the guarantee does not rest on this renderer.
        const lines = [
          textSpan('eg-body eg-swatch__name', way.title),
          textSpan('eg-caption eg-swatch__price', way.priceDisplay),
        ];

        const text = document.createElement('span');
        text.className = 'eg-swatch__text';
        text.append(...lines);

        const label = document.createElement('label');
        label.className = 'eg-swatch';
        label.setAttribute('for', input.id);
        label.append(imageNode(way.image, way.title, 'eg-swatch__image'), text);

        const cell = document.createElement('div');
        cell.className = 'eg-configurator__cell';
        cell.append(input, label);
        return cell;
      })
    );

    optionsEl.replaceChildren(grid);
  }

  function render() {
    const state = ladder.state();
    if (state.finished) {
      proceed();
      return;
    }

    titleEl.textContent = state.step.title;
    if (introEl) introEl.textContent = copy[state.step.key] ?? copy.default ?? '';

    renderStage(state);
    renderOptions(state);

    acceptBtn.disabled = busy || !state.canAdd;
    if (backBtn) {
      backBtn.hidden = !state.canGoBack;
      // Derived, never named: a third cover product added to the id map gets a
      // correct back label with no code change here.
      //
      // ARIA-LABEL, NOT textContent (Kyle, 2026-07-30) — the control is a
      // square icon-only button now, so this sentence is its ACCESSIBLE NAME
      // rather than its visible text. Writing `textContent` here would put the
      // words back inside the square, on top of the arrow.
      //
      // NO ARTICLE IN THE TEMPLATE — the titles carry their own. Production's
      // cover products are titled "the Heirloom Cover" and "the Minimalist
      // Sleeve" (lowercase article, the house voice), so `Show me the ${title}`
      // rendered "Show me the the Heirloom Cover again". Caught the first time
      // this surface was pointed at the real catalog; the dev store's titles
      // had no article, which is exactly the class of bug a dev-store green
      // cannot catch.
      if (state.canGoBack) backBtn.setAttribute('aria-label', `Show me ${state.previousTitle} again`);
    }
    if (!dialog.open) dialog.showModal();
  }

  acceptBtn.addEventListener('click', async () => {
    if (busy) return;
    const chosen = ladder.state().selected;
    if (!chosen) return;
    setBusy(true);
    try {
      await addVariantToCart(chosen.variantId);
      ladder.accept(); // Accept ends the ladder — the next offer never shows.
    } catch (error) {
      // Cart write failed: don't strand the shopper mid-ladder — proceed with
      // the Kit alone (recoverable from /cart) rather than blocking checkout.
      log('cart add failed, proceeding without the offer', error);
      ladder.skip(SKIP_REASON.ERROR);
    } finally {
      setBusy(false);
      render();
    }
  });

  declineBtn.addEventListener('click', () => {
    if (busy) return;
    // ladder_declined (2026-09-02) — read BEFORE decline() advances the
    // ladder's own index to the next step.
    const declinedStepKey = ladder.state().step.key;
    ladder.decline();
    track('ladder_declined', { step_key: declinedStepKey });
    render();
  });

  if (backBtn) {
    backBtn.addEventListener('click', () => {
      if (busy) return;
      ladder.back();
      render();
    });
  }

  // Escape = decline the current step (never a silent dismissal that loses the
  // shopper's place, never a trap).
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    if (busy) return;
    ladder.decline();
    render();
  });

  for (const form of forms) {
    // SYNCHRONOUS, so there is no window to guard. The steps were built at
    // init; this handler only has to open the dialog. The old async version
    // needed a `submitInFlight` flag because a second submit could land inside
    // the awaited config fetch while `ladder` was still null and race the first
    // submit's own dialog — with nothing awaited, `ladder !== null` is true by
    // the time any second submit can possibly run.
    form.addEventListener('submit', (event) => {
      if (ladder !== null) return; // A second submit while running: let it through.
      event.preventDefault();
      pendingForm = form;
      ladder = createLadder(steps);
      render();
      // ladder_shown (2026-09-02) — denominator for the pre-checkout upsell
      // funnel; the trigger form's submit is what opens the dialog.
      track('ladder_shown', { step_key: ladder.state().step.key });
    });
  }
}

document.querySelectorAll(SELECTORS.root).forEach(initLadder);
